import { Injectable } from '@angular/core'
import { HttpService, INew_TableAction, TableActionsService } from '@lla-platform/core/core-data-access'
import { HttpParams } from '@angular/common/http'
import { Observable, map } from 'rxjs'
import {
  IUsafOrder,
  IUsafOrderHistoryResponse,
  IUsafStockCheckRequest,
  IUsafStockCheckResponse,
  IUsafSubmitOrderRequest,
  IUsafSubmitOrderResponse
} from '../interfaces/usaf-order.interface'
import {
  IUsafAutoOrderAttemptsListResponse,
  IUsafLeftoversListResponse,
  IUsafOverdueOrdersListResponse,
  IUsafAutoOrderOverviewResponse
} from '../interfaces/usaf-auto-order.interface'
import {
  IUsafInvoicesListResponse,
  IUsafOrderLinesListResponse,
  IUsafOrdersListResponse,
  IUsafOrdersOverviewResponse
} from '../interfaces/usaf-lists.interface'
import {
  IUsafRefillLocation,
  IUsafRefillPlanResponse,
  IUsafRefillSubmitRequest,
  IUsafRefillSubmitResponse
} from '../interfaces/usaf-refill.interface'
import { TIRES_API_URL } from './tires-api-routes'

@Injectable({ providedIn: 'root' })
export class UsafService {
  constructor(
    private http: HttpService,
    private tableActionsService: TableActionsService
  ) {}

  getOrderHistory(locationId: string): Observable<IUsafOrder[]> {
    const params = new HttpParams().set('locationId', locationId)
    return this.http
      .get<IUsafOrderHistoryResponse>(TIRES_API_URL.tireUsafOrderHistory, { params })
      .pipe(map((res: IUsafOrderHistoryResponse) => res?.orders ?? []))
  }

  refreshOrderStatuses(locationId: string): Observable<IUsafOrder[]> {
    const params = new HttpParams().set('locationId', locationId).set('verbose', 'true')
    return this.http
      .post<IUsafOrderHistoryResponse>(TIRES_API_URL.tireUsafRefreshOrderStatuses, {}, { params })
      .pipe(map((res: IUsafOrderHistoryResponse) => res?.orders ?? []))
  }

  stockCheck(req: IUsafStockCheckRequest): Observable<IUsafStockCheckResponse> {
    const params = new HttpParams().set('verbose', 'true')
    return this.http.post(TIRES_API_URL.tireUsafStockCheck, req, { params })
  }

  submitOrder(order: IUsafSubmitOrderRequest): Observable<IUsafSubmitOrderResponse> {
    const params = new HttpParams().set('verbose', 'true')
    return this.http.post<IUsafSubmitOrderResponse>(TIRES_API_URL.tireUsafSubmitOrder, order, {
      params
    })
  }

  getOrdersList(info: INew_TableAction): Observable<IUsafOrdersListResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafOrdersList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getOrdersOverview(info: INew_TableAction): Observable<IUsafOrdersOverviewResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafOrdersOverview,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getOrderLinesList(info: INew_TableAction): Observable<IUsafOrderLinesListResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafOrderLinesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getInvoicesList(info: INew_TableAction): Observable<IUsafInvoicesListResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafInvoicesList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getAutoOrderAttemptsList(info: INew_TableAction): Observable<IUsafAutoOrderAttemptsListResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafAutoOrderAttemptsList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getAutoOrderOverview(info: INew_TableAction): Observable<IUsafAutoOrderOverviewResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafAutoOrderOverview,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getLeftoversList(info: INew_TableAction): Observable<IUsafLeftoversListResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafLeftoversList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getOverdueOrdersList(info: INew_TableAction): Observable<IUsafOverdueOrdersListResponse> {
    return this.http.post(
      TIRES_API_URL.tireUsafOverdueOrdersList,
      this.tableActionsService.createRequestPayload(info)
    )
  }

  getRefillLocations(): Observable<IUsafRefillLocation[]> {
    return this.http.get<IUsafRefillLocation[]>(TIRES_API_URL.tireUsafRefillLocations)
  }

  getRefillPlan(locationId: string): Observable<IUsafRefillPlanResponse> {
    const params = new HttpParams().set('locationId', locationId)
    return this.http.get<IUsafRefillPlanResponse>(TIRES_API_URL.tireUsafRefillPlan, { params })
  }

  submitRefill(request: IUsafRefillSubmitRequest): Observable<IUsafRefillSubmitResponse> {
    return this.http.post<IUsafRefillSubmitResponse>(TIRES_API_URL.tireUsafRefillSubmit, request)
  }

  getOrderDetail(orderId: string): Observable<IUsafOrder> {
    return this.http.get<IUsafOrder>(TIRES_API_URL.tireUsafOrderDetail(orderId))
  }
}
